/**
 * The narrative spine of a case — what happened, in order, and what we can see.
 *
 * A case file holds a feed, a graph, sagas and flows. All four are *indexes*:
 * they answer "what matched" sorted by time or by value. An exploit is not that
 * shape. It is a sequence of acts with a causal story — bought the position,
 * bridged it, inflated it, bridged it back, sold it, cashed out — and a reader
 * who does not already know the story cannot recover it from a table sorted
 * newest-first. That is why the public write-up of an incident reads better than
 * the product's own view of the same transactions.
 *
 * An act is deliberately small: a title, a note, the chains it happened on, and
 * the evidence pinned to it. Everything else the case already has.
 *
 * **The chains field is what makes an act honest.** Coverage is derived from it
 * ({@link actReach}), so an act on a chain no provider indexes is labelled as
 * unreachable rather than sitting there empty and reading as "nothing happened".
 * That distinction is the whole reason this is a first-class field rather than
 * something inferred from whatever evidence happened to be pinned: an act with no
 * evidence is exactly the case where the difference matters, and inferring the
 * chain from the evidence would be undefined precisely there.
 *
 * The Nesa / Cosmos-EVM exploit is the worked example. Five acts, two of them on
 * a chain the ingest cannot reach — and a case built only from what could be
 * fetched shows $50M appearing on Ethereum from nowhere.
 */
import { isIngestableChain, uncoveredChains } from "@authe/fe-commons/chains";
import { parseAssertedAmounts, type AssertedAmount } from "@authe/fe-commons/funds";

/** One step of the story. */
export interface CaseAct {
  /** Stable id, minted on create; the ordering is the array's. */
  id: string;
  title: string;
  /** What happened, in the author's words. May be empty while drafting. */
  note: string;
  /**
   * CAIP-2 chains this act took place on.
   *
   * Empty means "not stated" and is treated as unknown rather than as every
   * chain — a coverage claim about an act nobody has located would be a guess
   * presented as a fact.
   */
  chains: string[];
  /**
   * The phase this act belongs to (`"Bridge round trip"`); empty for none.
   *
   * Acts sharing a phase are read as ONE set — their movements netted together
   * (`stepFlow`, `@authe/fe-commons/funds`) — so the hop between two of them
   * cancels rather than being counted twice.
   */
  phase: string;
  /**
   * Amounts stated for what nobody can observe — the balance inflated on an
   * unindexed chain, the XMR sent into a swap service. Claims with a source,
   * shown beside the observed figures and never added to them.
   */
  asserted: AssertedAmount[];
}

/**
 * How many exhibits cite a given act, keyed by act id.
 *
 * Passed in rather than stored on the act. `CaseAct` used to carry
 * `evidence: string[]`, and nothing in the product ever wrote to it — so
 * {@link actReach} could not reach its `observed` branch and every step of every
 * story read as "not ingested yet" even for a case holding all of its
 * transactions. The citations live on the exhibit rows now
 * (`InvestigationEvidenceRow.slots`), which is where retention already looks:
 * an id array on the act would have been a citation pointing at a transaction
 * the case had since evicted.
 */
export type ActCitations = ReadonlyMap<string, number>;

/**
 * The citation counts, or `null` when the exhibit read has not answered.
 *
 * `null` is not an empty map and the difference is the point: an empty map is
 * the claim "no step cites anything", and a failed or in-flight read is not
 * entitled to make it. Every step of the story rendered "Not ingested yet" and
 * "0 cited" from a read that had simply failed — a wrong finding, in a product
 * whose whole job is telling a finding from an absence.
 */
export type MaybeActCitations = ActCitations | null;

/** No citations — every step cites nothing, and we know that. */
export const NO_CITATIONS: ActCitations = new Map<string, number>();

/** How much of an act the platform can actually show. */
export type ActReach =
  /** Evidence pinned; the act is on the record. */
  | { kind: "observed"; evidence: number }
  /** On chains we can fetch, but nothing pinned yet — ingest, then pin. */
  | { kind: "not-ingested" }
  /** On a chain no configured provider indexes. Annotation is the only record. */
  | { kind: "unreachable"; chains: string[] }
  /** No chains stated, so no claim either way. */
  | { kind: "unstated" }
  /**
   * The case's exhibits could not be read, so nothing can be said about this
   * step. Every other branch below turns on whether anything cites it, and
   * answering "nothing does" from a read that failed is a finding invented out
   * of a network error.
   */
  | { kind: "unknown" };

/**
 * What the platform can say about this act.
 *
 * Evidence wins over coverage: an act with pinned transactions is observed even
 * if it also names an unreachable chain, because somebody has already put the
 * record there — most likely by pasting it through the `inline` source, which
 * needs no provider. Reporting that as "unreachable" would call the reader's own
 * work invisible.
 */
export function actReach(act: CaseAct, citations: MaybeActCitations = NO_CITATIONS): ActReach {
  // Evidence decides every branch below, so without it there is no branch to
  // take — not the one that says nobody has ingested this yet.
  if (citations === null) return { kind: "unknown" };
  const cited = citations.get(act.id) ?? 0;
  if (cited > 0) return { kind: "observed", evidence: cited };
  if (act.chains.length === 0) return { kind: "unstated" };
  const uncovered = uncoveredChains(act.chains);
  // Unreachable only when NO chain on the act can be fetched. An act spanning
  // Ethereum and Nesa — a bridge leg — is ingestable in part, and calling it
  // unreachable would excuse leaving the half we can see unpinned.
  if (uncovered.length === act.chains.length) return { kind: "unreachable", chains: uncovered };
  return { kind: "not-ingested" };
}

/** One line explaining a reach, for the panel. Always says what to do next. */
export function reachExplanation(reach: ActReach): string {
  switch (reach.kind) {
    case "observed":
      return `${reach.evidence} transaction${reach.evidence === 1 ? "" : "s"} pinned as evidence.`;
    case "not-ingested":
      return "These chains can be fetched, but nothing is pinned yet — ingest the addresses on the Scope tab, then pin the transactions here.";
    case "unreachable":
      return "No configured provider indexes this chain, so a run cannot fetch it. Paste the transactions with the run dialog's Fixed payloads source and they join the case like any other.";
    case "unstated":
      return "No chain named, so there is nothing to say about whether this can be observed.";
    case "unknown":
      return "This case's evidence could not be read, so whether anything backs this step is unknown — not that nothing does.";
  }
}

/** The text fields of a step. What a reader has written, as opposed to chosen. */
export type ActTextField = "title" | "note" | "phase";

export const ACT_TEXT_FIELDS: readonly ActTextField[] = ["title", "note", "phase"];

/**
 * Where one field's unsaved text lives while it is still being typed.
 *
 * Every keystroke in the story used to be a whole-case PATCH. Holding the text
 * locally until it settles is what removes that, and keying it by act AND field
 * is what lets two fields be part-typed without one clobbering the other.
 */
export function actDraftKey(actId: string, field: ActTextField): string {
  return `${actId}\u0000${field}`;
}

/**
 * The acts as the reader has them on screen: saved values plus pending text.
 *
 * Every gesture that rebuilds the array — add a step, move one, delete one —
 * goes through this first. Rebuilding from the saved props instead would write
 * back the text as it was BEFORE the sentence somebody is in the middle of,
 * silently discarding it, which is the debounce's own way of losing work.
 *
 * Returns the acts unchanged when nothing is pending, so a settled panel
 * allocates nothing.
 */
export function applyActDrafts(
  acts: readonly CaseAct[],
  drafts: ReadonlyMap<string, string>,
): CaseAct[] {
  return acts.map((act) => {
    let next = act;
    for (const field of ACT_TEXT_FIELDS) {
      const value = drafts.get(actDraftKey(act.id, field));
      if (value !== undefined && value !== act[field]) next = { ...next, [field]: value };
    }
    return next;
  });
}

/**
 * What deleting this step would destroy, phrase by phrase — empty when nothing.
 *
 * Deleting a step was instant, silent and unrecoverable: one click removed a
 * paragraph somebody had written and cut every exhibit's citation loose with it,
 * with no confirmation and no undo. A confirmation is only worth reading if it
 * names what is being lost, so this produces the list rather than a boolean.
 *
 * `cited` is `null` when the exhibit read has not answered — which counts as
 * something to lose, because the alternative is destroying citations we could
 * not see.
 */
export function actDeletionLoss(act: CaseAct, cited: number | null): string[] {
  const lost: string[] = [];
  if (act.note.trim()) lost.push("what you wrote about it");
  if (cited === null) lost.push("any citations it carries (the case's evidence could not be read)");
  else if (cited > 0) lost.push(`${cited} cited transaction${cited === 1 ? "" : "s"}`);
  if (act.asserted.length > 0) {
    lost.push(`${act.asserted.length} asserted amount${act.asserted.length === 1 ? "" : "s"}`);
  }
  if (act.chains.length > 0) lost.push("the chains it names");
  return lost;
}

/**
 * Move one act up or down the story.
 *
 * The order IS the array — there is no `order` field to renumber — so a move is
 * a swap, and the whole array is what gets persisted. Returns the input
 * unchanged when the move would fall off either end, so a caller can bind the
 * control unconditionally and let it no-op rather than computing enabledness
 * twice.
 *
 * A story is the one view of a case that is ordered on purpose: the feed, the
 * graph and the flows all sort themselves, and this is the one a person arranges
 * to say what happened first. Being unable to reorder it meant the sequence was
 * decided by the order somebody happened to add the steps in — so getting it
 * right required deleting and retyping the ones that came out wrong, which loses
 * their citations with them.
 */
export function moveAct(acts: readonly CaseAct[], id: string, delta: -1 | 1): CaseAct[] {
  const from = acts.findIndex((act) => act.id === id);
  if (from < 0) return [...acts];
  const to = from + delta;
  if (to < 0 || to >= acts.length) return [...acts];
  const next = [...acts];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

export interface CaseCoverage {
  /** Every chain the case's acts name, de-duplicated in first-mention order. */
  chains: string[];
  /** The subset no configured provider can fetch. */
  uncovered: string[];
  /** Acts that can never carry fetched evidence. Zero while {@link reachKnown} is false. */
  unreachableActs: number;
  /** Acts on fetchable chains that have nothing pinned. Zero while {@link reachKnown} is false. */
  pendingActs: number;
  /**
   * Whether the two counts above mean anything.
   *
   * False when the exhibit read has not answered: the chains are still the
   * chains — they are on the acts — but how many steps are unreachable turns on
   * what cites them, and a zero from an unread collection is not a zero.
   */
  reachKnown: boolean;
}

/**
 * The case's coverage, in one read.
 *
 * Rendered ONCE at the top of the narrative rather than repeated per act,
 * because the sentence a reader needs before they interpret any of it is "two of
 * these five acts are on a chain we cannot fetch". Repeating that per act turns
 * a caveat about the case into noise on every row.
 */
export function caseCoverage(
  acts: readonly CaseAct[],
  citations: MaybeActCitations = NO_CITATIONS,
): CaseCoverage {
  const chains: string[] = [];
  const seen = new Set<string>();
  let unreachableActs = 0;
  let pendingActs = 0;

  for (const act of acts) {
    for (const chain of act.chains) {
      const key = chain.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      chains.push(chain);
    }
    const reach = actReach(act, citations);
    if (reach.kind === "unreachable") unreachableActs += 1;
    if (reach.kind === "not-ingested") pendingActs += 1;
  }

  return {
    chains,
    uncovered: uncoveredChains(chains),
    unreachableActs,
    pendingActs,
    reachKnown: citations !== null,
  };
}

/** Whether any chain in the set can be fetched at all. */
export function anyChainIngestable(chains: readonly string[]): boolean {
  return chains.some((chain) => isIngestableChain(chain));
}

/** A blank act, for the "add step" control. */
export function emptyAct(id: string): CaseAct {
  return { id, title: "", note: "", chains: [], phase: "", asserted: [] };
}

/**
 * Validate one persisted act, dropping anything malformed.
 *
 * Same discipline as `parseWatch`: `state.extra` is hand-editable and older
 * shapes exist, and an act with a non-array `chains` would break the panel that
 * renders it rather than simply being absent.
 *
 * A stored `evidence` array is ignored rather than migrated: nothing ever wrote
 * one, so every persisted value is `[]` and there is nothing in them to carry
 * across. Citations live on the exhibits now — see {@link ActCitations}.
 *
 * An UNTITLED act is not malformed — it is the first second of every step's
 * life. `add()` writes `emptyAct()`, the panel renders a title field for the
 * reader to type into, and its delete path has always special-cased a step with
 * no title. Dropping them here meant the one control that starts a story wrote
 * an act the server accepted (`_acts_of` requires only an id) and this reader
 * then refused: the new step appeared for about half a second and vanished, with
 * no error. "No story yet" was unreachable in both directions — you could not
 * leave it. The malformed cases below are still dropped; an empty title is a
 * state, not a defect.
 */
export function parseAct(raw: unknown, index: number): CaseAct | null {
  if (typeof raw !== "object" || raw === null) return null;
  const row = raw as Record<string, unknown>;
  const title = typeof row.title === "string" ? row.title : "";
  const id = typeof row.id === "string" && row.id ? row.id : `act-${index}`;
  const list = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === "string" && !!v) : [];
  return {
    id,
    title,
    note: typeof row.note === "string" ? row.note : "",
    chains: list(row.chains),
    phase: typeof row.phase === "string" ? row.phase : "",
    // Same leniency as the backend's coercer, so both agree on what survives.
    asserted: parseAssertedAmounts(row.asserted),
  };
}

export function parseActs(raw: unknown): CaseAct[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseAct).filter((a): a is CaseAct => a !== null);
}
